/**
 * Erro da API no envelope padrão de erro: `{ code, message, details, requestId }`.
 *
 * A mensagem vem pronta do backend, em português — a interface exibe o que
 * recebeu em vez de manter um segundo dicionário de mensagens que envelhece à
 * parte do servidor. `code` é o que a tela usa para decidir comportamento.
 */
export interface ApiErrorBody {
  code: string;
  message: string;
  details?: Record<string, unknown>;
  requestId?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Record<string, unknown>;
  readonly requestId?: string;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details;
    this.requestId = body.requestId;
  }

  /** Mensagens por campo devolvidas pelo ValidationPipe, quando houver. */
  get fieldErrors(): string[] {
    const fields = this.details?.fields;
    return Array.isArray(fields) ? fields.map(String) : [];
  }
}

const FALLBACK_MESSAGE = 'Não foi possível concluir a operação. Tente novamente.';

/**
 * Normaliza o corpo de erro. Uma falha de rede ou um 502 do proxy não devolvem o
 * envelope da API — sem esta normalização, a tela quebraria justamente quando algo
 * já está errado.
 */
export function toApiErrorBody(payload: unknown, status: number): ApiErrorBody {
  if (typeof payload === 'object' && payload !== null) {
    const candidate = payload as Partial<ApiErrorBody>;

    if (typeof candidate.code === 'string' && typeof candidate.message === 'string') {
      return {
        code: candidate.code,
        message: candidate.message,
        details: candidate.details,
        requestId: candidate.requestId,
      };
    }
  }

  return {
    code: status === 0 ? 'NETWORK_ERROR' : 'UNEXPECTED_RESPONSE',
    message:
      status === 0
        ? 'Não foi possível falar com o servidor. Verifique sua conexão.'
        : FALLBACK_MESSAGE,
  };
}
